"use client";

import type { PageBuilderOffice } from "~www/types/office";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";
import CareGiverLeadForm from "~www/components/LeadForm/CareGiver/CareGiverLeadForm";
import {
  handleDrawerClose,
  LeadFormDrawer,
} from "~www/components/LeadForm/CareGiver/LeadFormDrawer";
import { useMultiStepForm } from "~www/hooks/useMultiStepForm";

interface CaregiverDrawerWithSearchParamsProps {
  office?: PageBuilderOffice;
}

/**
 * Caregiver drawer component that uses useSearchParams().
 * Must be wrapped in Suspense due to Next.js requirements.
 */
export function CaregiverDrawerWithSearchParams({
  office,
}: CaregiverDrawerWithSearchParamsProps = {}) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const closeDrawer = useCallback(() => {
    router.push(handleDrawerClose({ pathname, searchParams }));
  }, [pathname, router, searchParams]);

  const careGiverForm = useMultiStepForm({
    finalStepIndex: 2,
    formId: "care-giver-form",
    initialStepIndex: 2,
    onFirst: closeDrawer,
  });

  const isCareGiverDrawerOpen = searchParams.get("leadVariant") === "CareGiver";

  return (
    <LeadFormDrawer
      description="Complete these steps to find care."
      hasBackButton
      isDrawerOpen={isCareGiverDrawerOpen}
      onBack={careGiverForm.goToPreviousStep}
      onClose={closeDrawer}
      title="Get Care for a Loved One"
    >
      {isCareGiverDrawerOpen && (
        <CareGiverLeadForm
          handleNext={careGiverForm.goToNextStep}
          isLastStep={careGiverForm.isLastStep}
          office={office}
        />
      )}
    </LeadFormDrawer>
  );
}
